/**
 * Board minutes for a capital increase, generated from the register.
 *
 * The document is **Norwegian** — decision 1 keeps generated, board-facing
 * output in the language it will be read and filed in, while the buttons and
 * labels around it stay English like the rest of the tool.
 *
 * ## Capital before is history, not today
 *
 * The one rule that makes these minutes trustworthy: *capital before* is summed
 * from the increases dated **before this one**, never from today's outstanding
 * total. Minutes for the seed round printed today must say what the company held
 * in 2023 — a document that reports figures which did not exist on the day it
 * covers is worse than no document, because it looks like a record.
 *
 * The same reasoning excludes transfers: shares changing hands between two
 * shareholders do not change the share capital, so they never appear in a
 * capital-increase minute. Buy-backs do reduce it, and are deliberately out of
 * scope here — retiring shares is its own resolution with its own minute, and
 * inventing that paragraph would be guessing at a document nobody has drawn.
 *
 * ## What it refuses to do
 *
 * Without a par value there is no share capital in kroner, and the generator
 * says so instead of printing a figure derived from the price paid. See
 * `types/company.ts`.
 */
import { useMemo, useState } from 'react'
import { Modal } from '@/shared/ui/Modal'
import { fmtDateAbs } from '@/shared/util/dates'
import { fmtKrExact, fmtKrPrice, fmtKrSum } from '@/shared/util/money'
import type { CompanyDetails } from '@/types/company'
import { effectivePrice, issuanceTotals, type Issuance, type Shareholder } from '@/types/ownership'

const nb = (n: number) => n.toLocaleString('nb-NO')

/** Norwegian long date, as a minute writes it: 15. august 2026. */
function noDate(iso: string): string {
  if (!iso) return '—'
  const months = [
    'januar', 'februar', 'mars', 'april', 'mai', 'juni',
    'juli', 'august', 'september', 'oktober', 'november', 'desember',
  ]
  const [y, m, d] = iso.split('-').map(Number)
  return y && m && d ? `${d}. ${months[m - 1]} ${y}` : iso
}

interface Row {
  name: string
  shares: number
  /** Of those, shares from an exercised option — the *herav opsjonskurs* column. */
  optionShares: number
  optionAmount: number
  amount: number
}

export function BoardMinutesModal({
  issuance,
  issuances,
  holders,
  company,
  onClose,
}: {
  issuance: Issuance
  /** All of them, oldest first — capital before is summed from the earlier ones. */
  issuances: Issuance[]
  holders: Shareholder[]
  company: CompanyDetails
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  const model = useMemo(() => {
    // Everything issued in a round that closed before this one. Dated strictly
    // before, so two rounds on one date do not count each other.
    const earlier = issuances.filter((i) => i.date && issuance.date && i.date < issuance.date)
    const sharesBefore = earlier.reduce((n, i) => n + issuanceTotals(i, holders).shares, 0)

    const totals = issuanceTotals(issuance, holders)
    const rows: Row[] = []
    for (const h of holders) {
      let shares = 0
      let amount = 0
      let optionShares = 0
      let optionAmount = 0
      /**
       * The strikes exercised against this round, so the *herav opsjonskurs*
       * column can find them.
       *
       * Shares taken up through the option programme are recorded as purchases
       * at the strike — that is what the register counts, and what the payment
       * request invoices. The grant records that it was spent, and is left
       * unpaid on purpose, because a paid exercise counts as shares on its own
       * and these are already counted as the purchase.
       *
       * Which meant this column had nothing to read: it looked for exercised
       * *and paid* grants, a state this round will never be in, so it printed a
       * dash beside every line including BOWL's 55 shares at kr 307,47.
       */
      const strikes = new Set(
        h.options
          .filter(
            (o) =>
              o.status === 'exercised' &&
              o.exerciseIssuanceId === issuance.id &&
              o.strikePrice != null,
          )
          .map((o) => o.strikePrice as number),
      )
      for (const p of h.purchases) {
        if (p.issuanceId !== issuance.id) continue
        shares += p.shares || 0
        // The holder's own agreed price where there is one. A round can carry
        // several — Emisjon 4 has the round's, two pre-agreed ones and an
        // option strike — and a tegningsliste that multiplied everybody by the
        // round price would state the wrong sum in a signed document.
        const each = effectivePrice(p, issuance) ?? 0
        amount += (p.shares || 0) * each
        if (strikes.has(each)) {
          optionShares += p.shares || 0
          optionAmount += (p.shares || 0) * each
        }
      }
      /**
       * A round still open lists what has been *subscribed*, not what it issued.
       *
       * Decision-wise this is the difference between a minute recording a
       * completed capital increase and a subscription list for a round in
       * progress. The register keeps them apart — subscriptions are not
       * purchases and count towards nothing — so the document has to read the
       * other array or come out empty, which is what it did for Emisjon 4.
       */
      if (issuance.status === 'ongoing') {
        for (const sub of h.subscriptions ?? []) {
          if (sub.issuanceId !== issuance.id) continue
          shares += sub.shares || 0
          amount += (sub.shares || 0) * (effectivePrice(sub, issuance) ?? 0)
        }
      }
      for (const o of h.options) {
        if (o.status !== 'exercised' || !o.paidDate || o.exerciseIssuanceId !== issuance.id) continue
        // Already counted above as a purchase at this strike. Both paths exist
        // because an exercise can be booked either way, but a grant that has a
        // purchase behind it must not add its shares a second time.
        if (o.strikePrice != null && strikes.has(o.strikePrice) && optionShares > 0) continue
        shares += o.count || 0
        optionShares += o.count || 0
        const at = (o.count || 0) * (o.strikePrice ?? 0)
        optionAmount += at
        amount += at
      }
      if (shares > 0) rows.push({ name: h.name, shares, optionShares, optionAmount, amount })
    }
    rows.sort((a, b) => b.shares - a.shares)

    const par = company.parValue
    /**
     * For an open round the shares are the subscribed ones.
     *
     * `issuanceTotals` counts purchases, which is right for a completed round
     * and zero for one still taking subscriptions — so a minute for Emisjon 4
     * would have listed 41 subscribers against 0 shares issued and a capital
     * increase of nothing. The figures below come from the rows the document
     * actually prints, so the sum at the bottom is the sum of the lines above
     * it whichever kind of round this is.
     */
    const open = issuance.status === 'ongoing'
    const issued = open ? rows.reduce((n, r) => n + r.shares, 0) : totals.shares
    return {
      open,
      rows,
      sharesBefore,
      sharesAfter: sharesBefore + issued,
      issued,
      subscribers: rows.length,
      amount: rows.reduce((n, r) => n + r.amount, 0),
      optionShares: totals.optionShares,
      capitalBefore: par === null ? null : sharesBefore * par,
      capitalAfter: par === null ? null : (sharesBefore + issued) * par,
      capitalIncrease: par === null ? null : issued * par,
      par,
    }
  }, [issuance, issuances, holders, company])

  /** What is missing before this can be a real document. */
  const missing = [
    !company.legalName && 'legal name',
    !company.orgNumber && 'organisation number',
    company.parValue === null && 'par value per share',
    company.board.length === 0 && 'board members',
  ].filter(Boolean) as string[]

  const text = useMemo(() => {
    const c = company
    const m = model
    const kap = (v: number | null) => (v === null ? '[pålydende mangler]' : fmtKrSum(v))
    const lines = [
      `PROTOKOLL FRA STYREMØTE I ${(c.legalName || '[selskapsnavn]').toUpperCase()}`,
      '',
      `Den ${noDate(issuance.date)} ble det avholdt styremøte i ${c.legalName || '[selskapsnavn]'}, org.nr. ${c.orgNumber || '[org.nr.]'}.`,
      '',
      'TIL STEDE',
      ...(c.board.length
        ? c.board.map((b) => `  ${b.name}${b.role ? ` — ${b.role}` : ''}`)
        : ['  [styremedlemmer mangler]']),
      '',
      'SAK 1 — GODKJENNING AV INNKALLING OG DAGSORDEN',
      'Innkalling og dagsorden ble enstemmig godkjent.',
      '',
      'SAK 2 — KAPITALFORHØYELSE',
      `Selskapets aksjekapital før forhøyelsen utgjør ${kap(m.capitalBefore)} fordelt på ${nb(m.sharesBefore)} aksjer, hver pålydende ${m.par === null ? '[pålydende mangler]' : fmtKrPrice(m.par)}.`,
      `Det tegnes ${nb(m.issued)} nye aksjer${issuance.pricePerShare === null ? '' : ` til kurs ${fmtKrPrice(issuance.pricePerShare)} per aksje`}.`,
      m.optionShares > 0
        ? `Av disse er ${nb(m.optionShares)} aksjer tegnet ved innløsning av opsjoner til opsjonskurs.`
        : '',
      `Samlet tegningsbeløp utgjør ${fmtKrExact(m.amount)}, fordelt på ${m.subscribers} tegner${m.subscribers === 1 ? '' : 'e'}.`,
      `Aksjekapitalen forhøyes med ${kap(m.capitalIncrease)}. Resten av tegningsbeløpet er overkurs.`,
      `Etter forhøyelsen utgjør aksjekapitalen ${kap(m.capitalAfter)} fordelt på ${nb(m.sharesAfter)} aksjer.`,
      '',
      'VEDTAK',
      'Styret vedtok kapitalforhøyelsen som beskrevet, med tegningsliste som gjengitt nedenfor.',
      '',
      'TEGNINGSLISTE',
      ...m.rows.map(
        (r) =>
          `  ${r.name} — ${nb(r.shares)} aksjer${r.optionShares > 0 ? ` (herav ${nb(r.optionShares)} til opsjonskurs)` : ''} — ${fmtKrExact(r.amount)}`,
      ),
      `  SUM — ${nb(m.issued)} aksjer — ${fmtKrExact(m.amount)}`,
      '',
      '',
      `Sted og dato: ______________________  (${fmtDateAbs(issuance.date)})`,
      '',
      ...(c.board.length
        ? c.board.flatMap((b) => ['', '______________________', `${b.name}${b.role ? `, ${b.role}` : ''}`])
        : ['', '______________________']),
    ]
    return lines.filter((l) => l !== '').join('\n').replace(/\n{3,}/g, '\n\n')
  }, [company, model, issuance])

  return (
    <Modal
      title={`Board minutes — ${issuance.name}`}
      onClose={onClose}
      width={780}
      footer={
        <>
          <span className="payreq__count">
            {model.subscribers} subscriber{model.subscribers === 1 ? '' : 's'} ·{' '}
            {nb(model.issued)} shares
          </span>
          <button
            className="btn btn--ghost"
            onClick={() => {
              void navigator.clipboard.writeText(text)
              setCopied(true)
              window.setTimeout(() => setCopied(false), 2000)
            }}
          >
            {copied ? 'Copied' : 'Copy text'}
          </button>
          <button className="btn btn--primary" onClick={() => window.print()}>
            Print
          </button>
        </>
      }
    >
      <div className="minutes">
        {missing.length > 0 && (
          <p className="recform__warn">
            Missing from Admin → Company details: {missing.join(', ')}. The document is generated
            with those parts marked, because a minute that invents an organisation number or a par
            value is worse than one that shows a gap.
          </p>
        )}

        {/* A round still open produces a subscription list, not a record of a
            capital increase that happened. Said at the top of the screen rather
            than left for the reader to infer from the round's status pill three
            tabs away — this document gets printed and signed. */}
        {model.open && (
          <p className="minutes__provisional">
            <strong>Emisjon not closed.</strong> This lists what has been subscribed so far —
            {' '}
            {nb(model.issued)} shares from {model.subscribers} subscriber
            {model.subscribers === 1 ? '' : 's'} — and no shares have been issued. It is a
            tegningsliste for a round in progress, not a minute recording a completed increase,
            and the figures move until the round closes.
          </p>
        )}

        <p className="recform__note minutes__lede">
          Norwegian, because it is filed in Norwegian. Every figure is read from the register — and
          the capital before this increase is summed from the rounds dated before it, not from
          today&rsquo;s total, so an old minute still reports the old company.
        </p>

        {/* The table is rendered rather than only written out, because the
            Tegningsliste is the part somebody checks line by line. */}
        <div className="own__table minutes__table">
          <div className="minutes__head">
            <div>Tegner</div>
            <div>Aksjer</div>
            <div>Herav opsjonskurs</div>
            <div>Beløp</div>
          </div>
          {model.rows.map((r) => (
            <div className="minutes__row" key={r.name}>
              <div className="own__trname">{r.name}</div>
              <div>{nb(r.shares)}</div>
              <div>{r.optionShares > 0 ? nb(r.optionShares) : '—'}</div>
              <div>{fmtKrExact(r.amount)}</div>
            </div>
          ))}
          <div className="minutes__row minutes__row--sum">
            <div>Sum</div>
            <div>{nb(model.issued)}</div>
            <div>{model.optionShares > 0 ? nb(model.optionShares) : '—'}</div>
            <div>{fmtKrExact(model.amount)}</div>
          </div>
        </div>

        <pre className="minutes__doc">{text}</pre>

        <p className="micro">
          Production exports .docx; this copies as plain text or prints.
        </p>
      </div>
    </Modal>
  )
}
